import { useState, useEffect } from "react";
import { getImageFromDB, isDBKey, fromDBRef } from "@/hooks/useImageDB";

/**
 * Resolves a single image reference.
 * If it's an idb: ref, loads from IndexedDB.
 * Otherwise returns the string as-is (URL or data URL).
 */
export function useResolvedImage(ref: string): string {
  const [resolved, setResolved] = useState<string>(() => {
    // If not an idb ref, we can resolve synchronously
    return isDBKey(ref) ? "" : ref;
  });

  useEffect(() => {
    if (!ref) {
      setResolved("");
      return;
    }
    if (isDBKey(ref)) {
      getImageFromDB(fromDBRef(ref)).then((dataUrl) => {
        setResolved(dataUrl ?? "");
      });
    } else {
      setResolved(ref);
    }
  }, [ref]);

  return resolved;
}

/**
 * Resolves multiple image references.
 */
export function useResolvedImages(refs: string[]): string[] {
  const [resolved, setResolved] = useState<string[]>(() =>
    refs.map((r) => (isDBKey(r) ? "" : r))
  );

  useEffect(() => {
    if (!refs || refs.length === 0) {
      setResolved([]);
      return;
    }
    Promise.all(
      refs.map(async (r) => {
        if (!r) return "";
        if (isDBKey(r)) {
          const dataUrl = await getImageFromDB(fromDBRef(r));
          return dataUrl ?? "";
        }
        return r;
      })
    ).then(setResolved);
  }, [refs.join(",")]);

  return resolved;
}
